/**
 * payrollRegisterKS — "Lista e Pagave", formulari zyrtar i Kosovës.
 *
 * Ekrani i pagave tregon një përmbledhje; ky është regjistri që dorëzohet dhe
 * nënshkruhet.
 *
 * Tatimi rillogaritet këtu sipas brezave të politikës fiskale aktive, edhe pse
 * regjistri shfaq vetëm totalin: `salaries.tax` mund të jetë ruajtur me një
 * politikë tjetër nga ajo në fuqi, dhe brezat janë e vetmja mënyrë për ta
 * nxjerrë shumën e saktë. Kjo është pjesa me rrezik real — një gabim nuk duket
 * në ekran, sepse totali del i njëjtë, dhe pastaj dorëzohet.
 */

const n = (v) => Number(v) || 0;
const round2 = (v) => +(Number(v) || 0).toFixed(2);

/** Baza e tatueshme: bruto minus kontributi i punëtorit (Ligji i KS). */
export function taxableBase(gross, employeeContribution) {
  return Math.max(0, (Number(gross) || 0) - (Number(employeeContribution) || 0));
}

/**
 * Tatimi i ndarë sipas brezave.
 *
 * Çdo brez tatohet vetëm mbi pjesën e të ardhurës që bie brenda tij — jo mbi
 * gjithë shumën. Një pagë prej 300 € nuk tatohet 8% mbi 300, por 0% mbi 80,
 * 4% mbi 170 dhe 8% mbi 50.
 *
 * @param {number} taxable
 * @param {Array<{From:number, To:number|null, Tax:number}>} ranges
 * @returns {{perRate: Object, byBracket: Array, total: number}}
 */
export function taxByBracket(taxable, ranges, { secondJob = false } = {}) {
  const base = Math.max(0, Number(taxable) || 0);
  const rates = [...(ranges || [])].map((r) => Number(r.Tax ?? r.tax ?? r.percent ?? 0) || 0);

  // Punësim i dytë: brezat — përfshirë atë të liruarin — i zbaton punëdhënësi
  // primar. Këtu tatohet e gjithë baza me normën e sipërme, pa brez të liruar.
  // Pa këtë, i njëjti punëtor do të merrte dy herë lirimin e parë të 80 €.
  if (secondJob && rates.length) {
    const top = Math.max(...rates);
    const amount = +(base * (top / 100)).toFixed(2);
    return {
      perRate: amount ? { [top]: amount } : {},
      byBracket: amount ? [{ from: 0, to: null, rate: top, base: +base.toFixed(2), amount }] : [],
      total: amount,
      secondJob: true,
    };
  }

  const sorted = [...(ranges || [])]
    .map((r) => ({
      from: Number(r.From ?? r.from ?? r.MinAmount ?? 0) || 0,
      to:   (r.To ?? r.to ?? r.MaxAmount) == null ? null : Number(r.To ?? r.to ?? r.MaxAmount),
      rate: Number(r.Tax ?? r.tax ?? r.percent ?? 0) || 0,
    }))
    .sort((a, b) => a.from - b.from);

  const byBracket = [];
  const perRate = {};
  let total = 0;

  for (const b of sorted) {
    const upper = b.to == null ? Infinity : b.to;
    const slice = Math.max(0, Math.min(base, upper) - b.from);
    if (slice <= 0) continue;
    const amount = +(slice * (b.rate / 100)).toFixed(2);
    byBracket.push({ from: b.from, to: b.to, rate: b.rate, base: +slice.toFixed(2), amount });
    perRate[b.rate] = +((perRate[b.rate] || 0) + amount).toFixed(2);
    total = +(total + amount).toFixed(2);
  }
  return { perRate, byBracket, total };
}

/**
 * Statistikat e pagave — çka thotë vërtet lista.
 *
 * Pesë shifra që lexohen pa shpjegim: sa është paga më e ulët, mesatarja, më e
 * larta, sa iu mbajt punëtorëve gjithsej dhe sa i kushtoi lista punëdhënësit.
 *
 * Zëvendëson numërimin sipas brezave (0–80 / 80–250 / …), që tregonte katër
 * numra pa kuptim praktik.
 */
export function salaryStats(rows) {
  const gross = rows.map((r) => Number(r.gross) || 0).filter((v) => v > 0).sort((a, b) => a - b);
  if (!gross.length) {
    return { count: 0, min: 0, max: 0, avg: 0, deductions: 0, employerCost: 0 };
  }
  const sum = gross.reduce((a, b) => a + b, 0);
  return {
    count: gross.length,
    min: round2(gross[0]),
    max: round2(gross[gross.length - 1]),
    avg: round2(sum / gross.length),
    // Sa iu mbajt punëtorëve gjithsej — tatim, kontribut, sigurim, avanc, ndalesa.
    deductions: round2(rows.reduce((a, r) => a + (Number(r.deductions) || 0), 0)),
    // Kostoja reale e punëdhënësit: bruto plus kontributi i tij. Kjo shifër nuk
    // duket askund tjetër te lista, ndonëse është ajo që del nga arka.
    employerCost: round2(rows.reduce(
      (a, r) => a + (Number(r.gross) || 0) + (Number(r.contribEmployer) || 0), 0)),
  };
}

/**
 * Ndërton modelin e regjistrit nga rreshtat e pagës.
 *
 * @param {Array} salaryRows — nga getSalariesByListId
 * @param {{ list:object, company:object, ranges:Array }} ctx
 */
export function buildRegisterKS(salaryRows, { list = {}, company = {}, ranges = [] } = {}) {
  const rows = (salaryRows || []).map((s, i) => {
    const base     = n(s.Salary ?? s.GrossSalary);
    const overtime = n(s.HourSalary);
    const bonus    = n(s.Bonus);
    // Bruto e vërtetë përfshin orët shtesë dhe bonusin — pikërisht si te
    // llogaritja e pagës. Vetëm paga bazë do ta nënvlerësonte bazën e tatimit.
    const gross    = round2(base + overtime + bonus);
    const empCtr   = n(s.employeeTaxFee ?? s.PensionEmployee);
    const emrCtr   = n(s.employerTaxFee ?? s.PensionEmployer);
    const health   = round2(n(s.HealthInsEmployee) + n(s.HealthInsPersonal) + n(s.HealthInsFamily));
    const advance  = n(s.Advances);
    const penalty  = n(s.Penalties);
    const taxable  = taxableBase(gross, empCtr);
    const secondJob = s.SecondJob === true || s.second_job === true;
    const foreign   = (s.Residency ?? s.residency) === 'foreign';
    const split    = taxByBracket(taxable, ranges, { secondJob });
    // Neto merret ai i ruajtur kur ekziston: rrumbullakimet e brezave mund të
    // ndryshojnë nga ai i pagës me disa cent, dhe regjistri duhet të tregojë
    // shumën që u pagua vërtet, jo një rillogaritje të re.
    const storedNet = n(s.neto ?? s.NetSalary);
    const first = s.FirstName || (s.FullName || '').split(' ')[0] || '';
    const last  = s.LastName  || (s.FullName || '').split(' ').slice(1).join(' ') || '';
    return {
      no: i + 1,
      firstName: first,
      lastName:  last,
      id:        s.NID || s.PersonalNumber || s.personal_number || '',
      // "Primar" shkruhet shprehimisht e jo lihet bosh: kolona bosh lexohej si
      // "e paplotësuar", ndërsa kjo është një zgjedhje me pasojë — punësimi
      // sekondar tatohet pa brezin e liruar, pra e njëjta bruto jep tatim tjetër.
      secondJob: secondJob ? 'Sekondar' : 'Primar',
      residency: foreign ? 'I huaj' : 'Vendas',
      base:      round2(base),
      overtime:  round2(overtime),
      bonus:     round2(bonus),
      gross:     round2(gross),
      contribEmployee: round2(empCtr),
      contribEmployer: round2(emrCtr),
      contribTotal:    round2(empCtr + emrCtr),
      taxable:   round2(taxable),
      tax4:      round2(split.perRate[4]  || 0),
      tax8:      round2(split.perRate[8]  || 0),
      tax10:     round2(split.perRate[10] || 0),
      taxTotal:  round2(split.total),
      health:    health,
      advance:   round2(advance),
      penalty:   round2(penalty),
      // Të gjitha ndalesat e punëtorit — kjo është shifra që, e zbritur nga
      // bruto, jep NETO-n. Pa të, kolonat nuk mblidheshin te totali.
      deductions: round2(empCtr + split.total + health + advance + penalty),
      net:       round2(storedNet || (gross - empCtr - split.total - health - advance - penalty)),
    };
  });

  const sum = (k) => round2(rows.reduce((a, r) => a + r[k], 0));
  return {
    header: {
      companyName: company.CompanyName || company.name || '',
      nrb:  company.NUI || '',              // Numri Unik i Biznesit
      nrf:  company.FiscalNumber || '',     // Numri Fiskal
      address: company.Address || '',
      phone:   company.Phone || '',
      bank:    company.BankName || company.Bank || '',
      account: company.BankAccount || '',
      director: company.Director || '',
      logo:    company.Logo || null,
      month: list.month || list.Month || null,
      year:  list.year  || list.Year  || null,
    },
    rows,
    totals: {
      base: sum('base'), overtime: sum('overtime'), bonus: sum('bonus'),
      gross: sum('gross'),
      health: sum('health'), advance: sum('advance'), penalty: sum('penalty'),
      deductions: sum('deductions'),
      contribEmployee: sum('contribEmployee'),
      contribEmployer: sum('contribEmployer'),
      contribTotal: sum('contribTotal'),
      taxable: sum('taxable'),
      tax4: sum('tax4'), tax8: sum('tax8'), tax10: sum('tax10'),
      taxTotal: sum('taxTotal'),
      net: sum('net'),
    },
    stats: salaryStats(rows),
  };
}

/**
 * Neto për pagesë — një rregull i vetëm për tërë sistemin.
 *
 * Përdoret nga fletëpagesa, nga njoftimi me email dhe nga raporti i pagave.
 * Neto e RUAJTUR fiton kur ekziston: ajo është shuma që u pagua vërtet, dhe një
 * rillogaritje e re do të ndryshonte me disa cent nga fletëpagesa e dërguar.
 * Kur mungon, mblidhet nga përbërësit — pikërisht si te `buildRegisterKS`.
 *
 * Ishte i shkruar brenda `SalariesList.js`; raporti i pagave e kishte lënë
 * jashtë dhe lexonte thjesht `NetSalary`, ndaj çdo rresht ku neto nuk ishte
 * ruajtur dilte 0 € — pa asnjë shenjë se mungonte.
 */
export function payslipNet(worker) {
  const gross   = parseFloat(worker.Salary || worker.salary || 0);
  const adv     = parseFloat(worker.Advances || worker.advances || 0);
  const extra   = parseFloat(worker.HourSalary || 0);
  const bonus   = parseFloat(worker.Bonus || worker.bonus || 0);
  const pen     = parseFloat(worker.Penalties || worker.penalties || 0);
  const pension = parseFloat(worker.employeeTaxFee || worker.PensionEmployee || 0);
  const tax     = parseFloat(worker.taxes || worker.Tax || 0);
  const healthIns = parseFloat(worker.HealthInsEmployee || 0);
  const storedNet = parseFloat(worker.neto || worker.NetSalary || 0);
  return storedNet || (gross + extra + bonus - pension - tax - healthIns - adv - pen);
}

/** Kolonat e formularit, në rendin zyrtar. */
export const REGISTER_COLUMNS = [
  { key: 'no',              label: 'Nr.',                     w: 5,  money: false },
  { key: 'firstName',       label: 'Emri',                    w: 18, money: false },
  { key: 'lastName',        label: 'Mbiemri',                 w: 18, money: false },
  { key: 'id',              label: 'ID',                      w: 15, money: false },
  { key: 'secondJob',       label: 'Punësimi',                w: 11, money: false },
  { key: 'residency',       label: 'Rezidenca',               w: 11, money: false },
  // Të ardhurat
  { key: 'base',            label: 'Paga bazë',               w: 12, money: true },
  { key: 'overtime',        label: 'Orë shtesë',              w: 11, money: true },
  { key: 'bonus',           label: 'Bonus',                   w: 11, money: true },
  { key: 'gross',           label: 'Bruto',                   w: 12, money: true },
  // Kontributet — normat 5%/5% hoqën: përsëriteshin në çdo rresht pa thënë asgjë.
  { key: 'contribEmployee', label: 'Kontributi punëtori',     w: 13, money: true },
  { key: 'contribEmployer', label: 'Kontributi punëdhënësi',  w: 13, money: true },
  // Ndalesat e punëtorit — bashkë me kontributin e tij japin NETO-n.
  { key: 'taxTotal',        label: 'Tatimi',                  w: 12, money: true },
  { key: 'health',          label: 'Sigurimi',                w: 11, money: true },
  { key: 'advance',         label: 'Avanci',                  w: 11, money: true },
  { key: 'penalty',         label: 'Ndalesa',                 w: 11, money: true },
  { key: 'net',             label: 'NETO',                    w: 13, money: true },
];
